import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Users, Edit, Trash2, Plus, BarChart3 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const BoardMemberPopup = () => {
  const [members, setMembers] = useState([
    {
      id: "1",
      name: "John Smith",
      age: 52,
      position: "CEO",
      company: "TechCorp Inc.",
      tenure: "4 years",
      specialty: "Technology Strategy",
      role: "president",
      gender: "male",
      education: "master",
    },
    {
      id: "2",
      name: "Sarah Johnson",
      age: 48,
      position: "CFO",
      company: "FinanceFirst Ltd.",
      tenure: "3 years",
      specialty: "Financial Oversight",
      role: "ordinary",
      gender: "female",
      education: "phd",
    },
    {
      id: "3",
      name: "Michael Chen",
      age: 45,
      position: "HR Director",
      company: "Internal",
      tenure: "2 years",
      specialty: "Human Resources",
      role: "employee",
      gender: "male",
      education: "bachelor",
    },
    {
      id: "4",
      name: "Emma Davis",
      age: 50,
      position: "Legal Counsel",
      company: "Davis & Associates",
      tenure: "5 years",
      specialty: "Legal & Compliance",
      role: "ordinary",
      gender: "female",
      education: "master",
    },
  ]);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const getRoleBadge = (role) => {
    const colors = {
      president: "bg-yellow-500 text-white",
      employee: "bg-blue-500 text-white",
      ordinary: "bg-green-500 text-white",
    };
    return colors[role] || "bg-muted";
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    setEditForm(member);
  };

  const handleSave = () => {
    if (editingId && editForm) {
      setMembers((prev) =>
        prev.map((member) =>
          member.id === editingId ? { ...member, ...editForm } : member
        )
      );
      setEditingId(null);
      setEditForm({});
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleDelete = (id) => {
    setMembers((prev) => prev.filter((member) => member.id !== id));
  };

  const handleAddNew = () => {
    const newMember = {
      id: Date.now().toString(),
      name: "New Member",
      age: 40,
      position: "Position",
      company: "Company",
      tenure: "0 years",
      specialty: "Specialty",
      role: "ordinary",
      gender: "male",
      education: "bachelor",
    };
    setMembers((prev) => [...prev, newMember]);
    setEditingId(newMember.id);
    setEditForm(newMember);
  };

  // Demographics data calculations
  const genderData = [
    { name: "Male", count: members.filter((m) => m.gender === "male").length },
    {
      name: "Female",
      count: members.filter((m) => m.gender === "female").length,
    },
  ];

  const ageGroupData = [
    {
      name: "30-40",
      count: members.filter((m) => m.age >= 30 && m.age < 40).length,
    },
    {
      name: "40-50",
      count: members.filter((m) => m.age >= 40 && m.age < 50).length,
    },
    {
      name: "50-60",
      count: members.filter((m) => m.age >= 50 && m.age < 60).length,
    },
    { name: "60+", count: members.filter((m) => m.age >= 60).length },
  ];

  const educationData = [
    {
      name: "Bachelor",
      count: members.filter((m) => m.education === "bachelor").length,
    },
    {
      name: "Master",
      count: members.filter((m) => m.education === "master").length,
    },
    { name: "PhD", count: members.filter((m) => m.education === "phd").length },
    {
      name: "Other",
      count: members.filter((m) => m.education === "other").length,
    },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          Board Overview
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl bg-background max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Users className="w-5 h-5 text-primary" />
            Board Member Overview
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger
              value="demographics"
              className="flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Demographics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Manage board member information, roles, and specialties
              </p>
              <Button
                onClick={handleAddNew}
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Member
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Tenure</TableHead>
                  <TableHead>Specialty</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      {editingId === member.id ? (
                        <Input
                          value={editForm.name || ""}
                          onChange={(e) =>
                            setEditForm({ ...editForm, name: e.target.value })
                          }
                          className="w-52"
                        />
                      ) : (
                        <span className="font-medium">{member.name}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === member.id ? (
                        <Input
                          type="number"
                          value={editForm.age || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              age: parseInt(e.target.value),
                            })
                          }
                          className="w-24"
                        />
                      ) : (
                        member.age
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === member.id ? (
                        <Input
                          value={editForm.position || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              position: e.target.value,
                            })
                          }
                          className="w-36"
                        />
                      ) : (
                        member.position
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === member.id ? (
                        <Input
                          value={editForm.company || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              company: e.target.value,
                            })
                          }
                          className="w-36"
                        />
                      ) : (
                        member.company
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === member.id ? (
                        <Input
                          value={editForm.tenure || ""}
                          onChange={(e) =>
                            setEditForm({ ...editForm, tenure: e.target.value })
                          }
                          className="w-28"
                        />
                      ) : (
                        member.tenure
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === member.id ? (
                        <Input
                          value={editForm.specialty || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              specialty: e.target.value,
                            })
                          }
                          className="w-44"
                        />
                      ) : (
                        member.specialty
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === member.id ? (
                        <select
                          value={editForm.role || ""}
                          onChange={(e) =>
                            setEditForm({ ...editForm, role: e.target.value })
                          }
                          className="border rounded px-2 py-1 bg-background text-foreground"
                        >
                          <option value="ordinary">Ordinary</option>
                          <option value="employee">Employee Rep</option>
                          <option value="president">President</option>
                        </select>
                      ) : (
                        <Badge className={getRoleBadge(member.role)}>
                          {member.role === "president"
                            ? "President"
                            : member.role === "employee"
                            ? "Employee Rep"
                            : "Ordinary"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === member.id ? (
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSave}
                            size="sm"
                            variant="default"
                          >
                            Save
                          </Button>
                          <Button
                            onClick={handleCancel}
                            size="sm"
                            variant="outline"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEdit(member)}
                            size="sm"
                            variant="ghost"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(member.id)}
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="demographics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">
                  Gender Distribution
                </h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={genderData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">
                  Age Groups
                </h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ageGroupData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--secondary))" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">
                  Education Level
                </h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={educationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--accent))" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default BoardMemberPopup;
